import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSiteData } from '@hooks/useSiteData';
import { useApiClient } from '@hooks/useApiClient';

// Action types
const ThemeActionTypes = {
  SET_THEME_CONFIG: 'SET_THEME_CONFIG',
  SET_THEME_CONFIG_LOADING: 'SET_THEME_CONFIG_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_SITE_DETAILS: 'SET_SITE_DETAILS',
  SET_CONSTANTS: 'SET_CONSTANTS',
  SET_URL_PARAMS: 'SET_URL_PARAMS',
};

// Default theme configuration
const defaultThemeConfig = {
  APP_TITLE: 'Fund Raisin',
  APP_DESCRIPTION: 'NFT Fundraising Platform',
  APP_BENEFICIARY: '',
  APP_ARTIST: '',
  APP_SPONSOR: '',
  APP_CAMPAIGN_NAME: '',
  APP_NFT_PLURAL: 'NFTs',
  APP_NFT_NAME: 'NFT',
  APP_ART_PREVIEW: 'preview.png',
  APP_PURPOSE: 'reach their goals',
  PRIMARY: '#007bff',
  SECONDARY: '#6c757d',
  GRADIENT_TOP: '#007bff',
  GRADIENT_BOTTOM: '#0056b3',
  DROPDOWN_HOVER: '#0056b3',
  DISABLED_CLOSE: '#6c757d',
  APP_MAIN_LOGO: 'yellow',
};

// Initial state
const initialThemeState = {
  themeConfig: defaultThemeConfig,
  themeConfigLoading: true,
  error: null,
  siteDetails: {},
  constants: {},
  isThemeInitialized: false,
  urlParams: {},
};

// Reducer
const themeReducer = (state, action) => {
  switch (action.type) {
    case ThemeActionTypes.SET_THEME_CONFIG_LOADING:
      return { ...state, themeConfigLoading: action.loading };

    case ThemeActionTypes.SET_ERROR:
      return { ...state, error: action.error, themeConfigLoading: false };

    case ThemeActionTypes.CLEAR_ERROR:
      return { ...state, error: null };

    case ThemeActionTypes.SET_THEME_CONFIG:
      return {
        ...state,
        themeConfig: { ...state.themeConfig, ...action.data },
        isThemeInitialized: true
      };

    case ThemeActionTypes.SET_SITE_DETAILS:
      return { ...state, siteDetails: action.data };

    case ThemeActionTypes.SET_CONSTANTS:
      return { ...state, constants: action.data };

    case ThemeActionTypes.SET_URL_PARAMS:
      return { ...state, urlParams: action.data };

    default:
      return state;
  }
};

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const { get } = useApiClient()
  const location = useLocation();
  const [state, dispatch] = useReducer(themeReducer, initialThemeState);
  const { loadSiteData } = useSiteData();

  const clearError = useCallback(() => {
    dispatch({ type: ThemeActionTypes.CLEAR_ERROR });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: ThemeActionTypes.SET_ERROR, error });
  }, []);

  // Parse URL query parameters
  const parseUrlParams = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const params = {};
    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }
    return params;
  }, [location.search]);

  // Update URL parameters when location changes
  useEffect(() => {
    const urlParams = parseUrlParams();
    dispatch({ type: ThemeActionTypes.SET_URL_PARAMS, data: urlParams });
  }, [parseUrlParams]);

  // Load theme configuration on mount
  useEffect(() => {
    const initializeTheme = async () => {
      if (state.isThemeInitialized && Object.keys(state.urlParams).length === 0) return;
      try {
        // 1. Load constants
        const constants = await get('/constants');

        // 2. Load theme configuration, potentially using project-code from URL
        let themeData = {};
        const projectCode = state.urlParams?.projectCode;

        let configEndpoint = '/configuration';
        if (projectCode) {
          // If project-code is provided, you might want to load a specific configuration
          configEndpoint = `/configuration/preview/${projectCode}`;
        }

        themeData = await get(configEndpoint);

        dispatch({ type: ThemeActionTypes.SET_CONSTANTS, data: constants });
        dispatch({ type: ThemeActionTypes.SET_THEME_CONFIG, data: themeData });

        const siteData = await loadSiteData();
        dispatch({ type: ThemeActionTypes.SET_SITE_DETAILS, data: siteData.siteDetails });
        dispatch({ type: ThemeActionTypes.SET_THEME_CONFIG_LOADING, loading: false });

      } catch (error) {
        console.error('Failed to initialize theme:', error);
        dispatch({
          type: ThemeActionTypes.SET_ERROR,
          error: typeof error === 'string' ? error : error.message
        });
      }
    };

    initializeTheme();
  }, [loadSiteData, state.isThemeInitialized, state.urlParams, get]);

  // Apply CSS variables when theme config changes
  useEffect(() => {
    if (state.themeConfig) {
      const root = document.documentElement;
      root.style.setProperty('--web-primary-color', state.themeConfig.PRIMARY);
      root.style.setProperty('--web-secondary-color', state.themeConfig.SECONDARY);
      root.style.setProperty('--gradient-color-top', state.themeConfig.GRADIENT_TOP);
      root.style.setProperty('--gradient-color-bottom', state.themeConfig.GRADIENT_BOTTOM);
      root.style.setProperty('--dropdown-hover-color', state.themeConfig.DROPDOWN_HOVER);
      root.style.setProperty('--disabled-button-color', state.themeConfig.DISABLED_CLOSE);
    }
  }, [state.themeConfig]);

  // Helper function to get specific URL parameter
  const getUrlParam = useCallback((key, defaultValue = null) => {
    return state.urlParams[key] || defaultValue;
  }, [state.urlParams]);

  // Context value - memoized to prevent re-renders
  const contextValue = useMemo(() => ({
    // State
    ...state,
    setError,
    clearError,
    // URL parameter helpers
    getUrlParam,
    projectCode: getUrlParam('project-code'),
  }), [state, setError, clearError, getUrlParam]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};
